import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { colors } from 'styles/tokens'
import { getDropdownZIndex } from 'styles/z-index'
import { SalesStage } from 'models/sales_stages'
import {
  getStageColor,
  isTerminalStage,
  INACTIVE_STAGE_COLOR,
} from './stageColors'

// ─── Full-width 3-chevron stage bar ──────────────────────────────────────────

interface StageChevronBarProps {
  /** Sorted list of all sales stages. */
  stages: SalesStage[]
  /** Currently active stage ID (highlighted in the center). */
  activeStageId: number | null
  /** Called when the user selects a different stage. */
  onStageChange: (stageId: number) => void
}

/**
 * Reusable 3-chevron stage progress bar: previous → current → next.
 *
 * Previous/next chevrons have dropdown menus listing reachable stages.
 * Terminal stages (Closed Won, Closed Lost, Churn) are treated as end-states:
 * they appear in the "next" dropdown but never show a forward neighbor.
 */
export function StageChevronBar({
  stages,
  activeStageId,
  onStageChange,
}: StageChevronBarProps) {
  if (stages.length === 0) return null

  const activeIndex = stages.findIndex((s) => s.id === activeStageId)
  const currentStage = activeIndex >= 0 ? stages[activeIndex] : null
  const currentIsTerminal = currentStage ? isTerminalStage(currentStage) : false

  // Separate regular (pipeline) stages from terminal stages
  const regularStages = stages.filter((s) => !isTerminalStage(s))
  const terminalOthers = stages.filter(
    (s) => isTerminalStage(s) && s.id !== activeStageId
  )

  // Determine neighbors
  let prevStage: SalesStage | null = null
  let nextStage: SalesStage | null = null

  if (currentIsTerminal) {
    prevStage =
      regularStages.length > 0 ? regularStages[regularStages.length - 1] : null
    nextStage = null
  } else {
    const regularIndex = regularStages.findIndex((s) => s.id === activeStageId)
    prevStage = regularIndex > 0 ? regularStages[regularIndex - 1] : null
    nextStage =
      regularIndex >= 0 && regularIndex < regularStages.length - 1
        ? regularStages[regularIndex + 1]
        : null
  }

  // Build dropdown options
  let prevOptions: SalesStage[]
  let nextOptions: SalesStage[]

  if (currentIsTerminal) {
    // When on a terminal stage, the left dropdown includes all regular stages
    // plus the other terminal stages so user can switch between them
    prevOptions = [...regularStages, ...terminalOthers]
    nextOptions = []
  } else {
    const regularIndex = regularStages.findIndex((s) => s.id === activeStageId)
    prevOptions = regularIndex > 0 ? regularStages.slice(0, regularIndex) : []
    const regularAfter =
      regularIndex >= 0 ? regularStages.slice(regularIndex + 1) : []
    nextOptions = [...regularAfter, ...terminalOthers]
  }

  // Compute colors
  const total = stages.length
  const currentColors = currentStage
    ? getStageColor(currentStage, activeIndex, total)
    : { bg: INACTIVE_STAGE_COLOR, text: colors['fg'] }
  const prevColors = prevStage
    ? getStageColor(prevStage, stages.indexOf(prevStage), total)
    : undefined
  const nextColors = nextStage
    ? getStageColor(nextStage, stages.indexOf(nextStage), total)
    : undefined

  // When no stage is selected, show a single chevron with all stages as options
  if (!currentStage) {
    return (
      <div className="flex items-center pr-3">
        <ChevronWithDropdown
          label="Select Sales Stage"
          stageColors={{ bg: INACTIVE_STAGE_COLOR, text: colors['fg'] }}
          isFirst
          isLast
          options={stages}
          stages={stages}
          onSelect={onStageChange}
        />
      </div>
    )
  }

  return (
    <div className="flex items-center pr-3">
      {/* Previous */}
      {prevStage && prevColors && (
        <ChevronWithDropdown
          label={prevStage.stage}
          stageColors={prevColors}
          isFirst
          isLast={false}
          options={prevOptions}
          stages={stages}
          onSelect={onStageChange}
        />
      )}

      {/* Current */}
      <div className="flex-1 -mr-3" style={{ minWidth: 0 }}>
        <StageChevron
          label={currentStage.stage}
          color={currentColors.bg}
          textColor={currentColors.text}
          fontWeight="font-bold"
          isFirst={!prevStage}
          isLast={!nextStage}
          showCaret={false}
        />
      </div>

      {/* Next */}
      {nextStage && nextColors && (
        <ChevronWithDropdown
          label={nextStage.stage}
          stageColors={nextColors}
          isFirst={false}
          isLast
          options={nextOptions}
          stages={stages}
          onSelect={onStageChange}
        />
      )}
    </div>
  )
}

// ─── Chevron with dropdown ───────────────────────────────────────────────────

interface ChevronWithDropdownProps {
  label: string
  stageColors: { bg: string; text: string }
  isFirst: boolean
  isLast: boolean
  options: SalesStage[]
  /** Full sorted stages list, used to compute dropdown item colors. */
  stages: SalesStage[]
  onSelect: (stageId: number) => void
}

/** A chevron that opens a dropdown menu with selectable stages. */
function ChevronWithDropdown({
  label,
  stageColors,
  isFirst,
  isLast,
  options,
  stages,
  onSelect,
}: ChevronWithDropdownProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasOptions = options.length > 0
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div
      ref={containerRef}
      className="flex-1 -mr-3 relative"
      style={{ minWidth: 0 }}
    >
      <div
        className={cn(hasOptions && 'cursor-pointer')}
        onClick={hasOptions ? () => setOpen((o) => !o) : undefined}
      >
        <StageChevron
          label={label}
          color={stageColors.bg}
          textColor={stageColors.text}
          fontWeight="font-medium"
          isFirst={isFirst}
          isLast={isLast}
          showCaret={hasOptions}
          caretBg={open ? 'rgba(255,255,255,0.3)' : undefined}
        />
      </div>

      {open && options.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-surface border border-line rounded-md shadow-lg py-1 max-h-48 overflow-y-auto"
          style={{ zIndex }}
        >
          {options.map((stage) => {
            const idx = stages.findIndex((s) => s.id === stage.id)
            const colors = getStageColor(stage, idx, stages.length)
            return (
              <button
                key={stage.id}
                type="button"
                className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-fg hover:bg-surface-alt transition-colors"
                onClick={() => {
                  onSelect(stage.id)
                  setOpen(false)
                }}
              >
                <span
                  className="size-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colors.bg }}
                />
                {stage.stage}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Single chevron shape ────────────────────────────────────────────────────

interface StageChevronProps {
  label: string
  color: string
  textColor?: string
  fontWeight: string
  isFirst: boolean
  isLast: boolean
  showCaret: boolean
  caretBg?: string
}

/** Individual chevron-shaped stage indicator with dynamic fill and text color. */
function StageChevron({
  label,
  color,
  textColor = colors['fg'],
  fontWeight,
  isFirst,
  isLast,
  showCaret,
  caretBg,
}: StageChevronProps) {
  return (
    <div className="flex items-center" style={{ height: 32 }}>
      {!isFirst && (
        <svg
          width="12"
          height="32"
          viewBox="0 0 12 32"
          className="flex-shrink-0"
        >
          <path d="M12 0 L12 32 L0 32 L12 16 L0 0 Z" fill={color} />
        </svg>
      )}

      <div
        className="flex-1 flex items-center justify-center gap-1 min-w-0 transition-colors"
        style={{
          backgroundColor: color,
          height: 32,
          borderTopLeftRadius: isFirst ? 4 : 0,
          borderBottomLeftRadius: isFirst ? 4 : 0,
          borderTopRightRadius: isLast ? 4 : 0,
          borderBottomRightRadius: isLast ? 4 : 0,
        }}
      >
        <span
          className={cn(
            'text-sm truncate transition-[font-weight]',
            fontWeight
          )}
          style={{ color: textColor }}
        >
          {label}
        </span>
        {showCaret && (
          <div
            className="flex items-center justify-center flex-shrink-0 size-5 rounded transition-colors"
            style={caretBg ? { backgroundColor: caretBg } : undefined}
          >
            <ChevronDown size={14} style={{ color: textColor }} />
          </div>
        )}
      </div>

      {!isLast && (
        <svg
          width="12"
          height="32"
          viewBox="0 0 12 32"
          className="flex-shrink-0"
        >
          <path d="M0 0 L12 16 L0 32 Z" fill={color} />
        </svg>
      )}
    </div>
  )
}
